<template>
  <div class="epoch-dashboard" :class="barTone">
    <div class="epoch-shell">
      <div v-if="!showInitPanel" class="epoch-bar" role="button" tabindex="0" @click="openDetail"
        @keyup.enter="openDetail">
        <div class="epoch-bar-cells">
          <div class="epoch-cell is-run">
            <span class="cell-label">求解器</span>
            <span class="cell-value-row">
              <span class="pulse" v-if="solverSearching"></span>
              <span class="cell-value" :class="solverTone">{{ solverText }}</span>
            </span>
          </div>
          <div class="epoch-cell">
            <span class="cell-label">计划</span>
            <span class="cell-value">{{ runStateText }}</span>
          </div>
          <div class="epoch-cell">
            <span class="cell-label">Epoch</span>
            <span class="cell-value mono">{{ displayNum(doc.epoch) }}</span>
          </div>
          <div class="epoch-cell">
            <span class="cell-label">快照序号</span>
            <span class="cell-value mono">{{ displayNum(doc.snapshotSeq) }}</span>
          </div>
          <div class="epoch-cell">
            <span class="cell-label">最近求解</span>
            <span class="cell-value" :class="statusTone">{{ statusText }}</span>
          </div>
          <div class="epoch-cell">
            <span class="cell-label">脏标记</span>
            <span class="cell-value" :class="dirtyTone">{{ dirtyText }}</span>
          </div>
          <div class="epoch-cell" @click.stop="openDetail('events')">
            <span class="cell-label">开解后事件</span>
            <span class="cell-value mono" :class="eventAfterTone">{{ eventAfterText }}</span>
          </div>
          <div class="epoch-cell">
            <span class="cell-label">最近应用</span>
            <span class="cell-value time">{{ formatTime(doc.lastAppliedAt) }}</span>
          </div>
        </div>
        <div class="epoch-cell">
          <span class="cell-label">最近开解</span>
          <span class="cell-value time">{{ formatTime(doc.lastSolveStartedAt) }}</span>
        </div>
        <div class="epoch-cell">
          <span class="cell-label">耗时</span>
          <span class="cell-value time">{{ onlineElapsedText }}</span>
        </div>
        <div class="epoch-actions" @click.stop>
          <el-button type="primary" plain size="mini" :loading="enqueueing" :disabled="!date || initPaused"
            @click="enqueueSolve">立即求解</el-button>
          <el-button size="mini" :loading="inspecting" :disabled="!date" @click="inspectPlan">检测</el-button>
        </div>
      </div>

      <div class="epoch-ledger" v-if="showInitPanel" role="button" tabindex="0" @click="openDetail"
        @keyup.enter="openDetail">
        <div class="epoch-ledger-cells" v-if="hasLedger">
          <div class="epoch-cell">
            <span class="cell-label">账本</span>
            <span class="cell-value">{{ ledgerStatus || "-" }}</span>
          </div>
          <div class="epoch-cell">
            <span class="cell-label">已派</span>
            <span class="cell-value mono">{{ displayNum(assignedPerson) }} 人</span>
          </div>
          <div class="epoch-cell">
            <span class="cell-label">未派</span>
            <span class="cell-value mono">{{ displayNum(unassignedPerson) }} 人</span>
          </div>
          <div class="epoch-cell">
            <span class="cell-label">行程</span>
            <span class="cell-value mono">{{ displayNum(tripCount) }} 个</span>
          </div>
        </div>
        <div class="epoch-cell">
          <span class="cell-label">初始化开始</span>
          <span class="cell-value time">
            {{ formatTime(initStartAt) }}
            <template v-if="!initStartedAt && deriveOperator">（{{ deriveOperator }}）</template>
          </span>
        </div>
        <div class="epoch-cell">
          <span class="cell-label">耗时</span>
          <span class="cell-value time">{{ initElapsedText }}</span>
        </div>
        <div class="epoch-extra" v-if="hasExtra" @click.stop>
          <slot name="extra"></slot>
        </div>
      </div>
      <div class="epoch-wheel" @click.stop>
        <slot name="wheel"></slot>
      </div>
    </div>

    <el-dialog :title="detailTab === 'events' ? '开解后事件' : '在线计划 Epoch'" :visible.sync="detailVisible"
      :width="detailTab === 'events' ? '1280px' : '720px'" append-to-body>
      <el-tabs v-model="detailTab" class="detail-tabs">
        <el-tab-pane name="status" label="Epoch 状态">
          <el-descriptions :column="2" border size="small">
            <el-descriptions-item v-for="item in detailItems" :key="item.key" :label="item.label"
              :span="item.span || 1">
              <div class="detail-value">{{ item.value }}</div>
              <div class="detail-hint">{{ item.hint }}</div>
            </el-descriptions-item>
          </el-descriptions>
        </el-tab-pane>
        <el-tab-pane name="events">
          <span slot="label">开解后事件（{{ eventAfterCount }}）</span>
          <div class="delta-head">
            <span class="delta-meta">
              seq &gt; {{ displayNum(deltaSnapshotSeq) }}
              · 共 {{ deltaEvents.length }} 条
              <template v-if="structuralAfterCount"> · 结构 {{ structuralAfterCount }}</template>
            </span>
          </div>
          <el-table v-loading="eventsLoading" :data="deltaEvents" size="small" stripe max-height="420"
            empty-text="本次求解后暂无增量事件">
            <el-table-column label="序号" prop="seq" width="72" align="center">
              <template slot-scope="scope">
                <span class="mono">{{ scope.row.seq }}</span>
              </template>
            </el-table-column>
            <el-table-column label="时间" width="160">
              <template slot-scope="scope">{{ formatTime(scope.row.at) }}</template>
            </el-table-column>
            <el-table-column label="类型" width="88">
              <template slot-scope="scope">
                <el-tag size="mini" :type="eventTypeMeta(scope.row.type).tag">
                  {{ eventTypeMeta(scope.row.type).label }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="影响" width="72">
              <template slot-scope="scope">
                <el-tag size="mini" :type="scope.row.structural ? 'danger' : 'info'">
                  {{ scope.row.structural ? "结构" : "软" }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="来源" min-width="320">
              <template slot-scope="scope">
                <div class="source-stack" v-if="sourceView(scope.row.source).length">
                  <div class="source-stack__body" @click.stop>
                    <div class="source-stack__line" v-for="(item, idx) in sourceView(scope.row.source)" :key="idx"
                      :class="{ 'is-origin': item.origin }">
                      <span class="source-stack__arrow" v-if="idx && item.frame !== '…'">←</span>
                      <el-tag v-if="item.origin" size="mini" type="warning" class="source-stack__origin">入口</el-tag>
                      <span class="mono">{{ item.frame }}</span>
                    </div>
                  </div>
                  <el-button type="text" size="mini" class="source-stack__copy"
                    @click.stop="copySource(scope.row.source)">复制</el-button>
                </div>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column label="订单" min-width="110">
              <template slot-scope="scope"><el-link v-operate-order:transaction="scope.row.orderId" type="primary">{{
                scope.row.orderId
                  }}</el-link></template>
            </el-table-column>
            <el-table-column label="司机" min-width="110">
              <template slot-scope="scope">{{ displayId(scope.row.driverId) }}</template>
            </el-table-column>
            <el-table-column label="规划ID" min-width="110">
              <template slot-scope="scope">{{ displayId(scope.row.actPlanId) }}</template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </el-dialog>

    <el-dialog title="规划检测" :visible.sync="inspectVisible" width="760px" append-to-body>
      <div v-if="inspectResult" class="inspect-summary">
        <span :class="inspectResult.ok ? 'ok' : 'bad'">{{ inspectResult.ok ? "通过" : "未通过" }}</span>
        <span class="muted">{{ inspectResult.date }} · {{ inspectResult.planCount }} 个规划 · {{ inspectResult.driverCount
        }}
          名司机</span>
      </div>
      <pre v-if="inspectResult && inspectResult.text" class="inspect-text">{{ inspectResult.text }}</pre>
    </el-dialog>
  </div>
</template>

<script>
import { getEvPlanDeltaEvents, getEvPlanEpoch, getEvPlanInspect, submitEvOnlineSolve } from "@/api/ev_solver";
import { DateUtil, Toolkit } from "@/util";

const POLL_MS = 3000;

const STATUS_MAP = {
  OPTIMAL: "最优已应用",
  FEASIBLE: "可行已应用",
  APPLIED: "已应用",
  ORDER_MODIFYING: "改签中",
  ERROR: "求解失败",
  APPLY_ERROR: "应用失败",
  APPLY_LOCK_FAIL: "应用锁失败",
  CANCELLED: "已取消",
  STALE_DISCARD: "版本过期丢弃",
  STALE_DEAD: "僵死已清除",
  STRUCTURAL_DISCARD: "结构脏丢弃",
  NULL: "无可行解",
  FAILED: "无可行解",
  INIT_BLOCKED: "初始化中已暂停",
  INIT: "初始化已应用",
  NO_VEHICLES: "无车辆",
  EMPTY_NETWORK: "空网络",
  COOLDOWN: "冷却中"
};

const EVENT_TYPE_MAP = {
  BOOK: { label: "订票", tag: "" },
  CANCEL: { label: "退票", tag: "info" },
  CHANGE: { label: "改签", tag: "danger" },
  DISPATCH: { label: "派单", tag: "danger" },
  LEAVE: { label: "请假", tag: "warning" },
  SCHEDULING: { label: "排班", tag: "warning" }
};

function emptyDoc(date) {
  return {
    id: date || "",
    date: date || "",
    epoch: 0,
    solving: false,
    snapshotAt: null,
    snapshotSeq: 0,
    dirty: false,
    dirtyStructural: false,
    eventAfterCount: 0,
    lastAppliedAt: null,
    lastSolveStartedAt: null,
    lastSolveStatus: "",
    pendingRequeue: false,
    solverAlive: false,
    queued: false,
    currentRunId: "",
    initHorizon: false,
    initInFlight: false
  };
}

export default {
  name: "EvEpochDashboard",
  props: {
    date: {
      type: String,
      default: ""
    },
    hasLedger: {
      type: Boolean,
      default: false
    },
    ledgerStatus: {
      type: String,
      default: ""
    },
    assignedPerson: {
      type: Number,
      default: 0
    },
    unassignedPerson: {
      type: Number,
      default: 0
    },
    tripCount: {
      type: Number,
      default: 0
    },
    deriveAt: {
      type: [String, Number, Date],
      default: null
    },
    deriveOperator: {
      type: String,
      default: ""
    },
    solveTimeText: {
      type: String,
      default: ""
    },
    hasExtra: {
      type: Boolean,
      default: false
    },
    initStartedAt: {
      type: [String, Number, Date],
      default: null
    },
    initFinishedAt: {
      type: [String, Number, Date],
      default: null
    }
  },
  data() {
    return {
      doc: emptyDoc(""),
      detailVisible: false,
      detailTab: "status",
      pollTimer: null,
      clock: Date.now(),
      fetching: false,
      eventsLoading: false,
      eventsReqId: 0,
      enqueueing: false,
      inspecting: false,
      inspectVisible: false,
      inspectResult: null,
      deltaEvents: [],
      deltaSnapshotSeq: 0
    };
  },
  computed: {
    initPaused() {
      return !!this.doc.initHorizon || !!this.doc.initInFlight;
    },
    showInitPanel() {
      return this.hasExtra || this.initPaused;
    },
    onlineSolving() {
      return !!this.doc.solverAlive || !!this.doc.queued;
    },
    onlineElapsedText() {
      this.clock;
      const start = this.parseTime(this.doc.lastSolveStartedAt);
      if (!start) {
        return "-";
      }
      if (this.onlineSolving) {
        return this.formatDuration(Math.max(0, this.clock - start));
      }
      const applied = this.parseTime(this.doc.lastAppliedAt);
      if (applied && applied >= start) {
        return this.formatDuration(applied - start);
      }
      return "-";
    },
    initStartAt() {
      return this.initStartedAt || this.deriveAt;
    },
    initElapsedText() {
      this.clock;
      const start = this.parseTime(this.initStartedAt);
      if (start) {
        const end = this.parseTime(this.initFinishedAt) || this.clock;
        return this.formatDuration(Math.max(0, end - start));
      }
      return this.solveTimeText || "-";
    },
    solverSearching() {
      return !!this.doc.solverAlive && !this.initPaused;
    },
    solverText() {
      if (this.doc.initInFlight) {
        return "初始化中";
      }
      if (this.doc.initHorizon) {
        return "初始化暂停";
      }
      if (this.doc.solverAlive) {
        return "搜索中";
      }
      if (this.doc.queued) {
        return "排队中";
      }
      return "未运行";
    },
    solverTone() {
      if (this.doc.initInFlight || this.doc.initHorizon) {
        return "is-warn";
      }
      if (this.doc.solverAlive) {
        return "is-ok";
      }
      if (this.doc.queued) {
        return "is-warn";
      }
      return "";
    },
    runStateText() {
      if (this.doc.initInFlight) {
        return "初始化中";
      }
      if (this.doc.initHorizon) {
        return "初始化暂停";
      }
      if (this.doc.dirtyStructural) {
        return "结构脏";
      }
      if (this.doc.pendingRequeue) {
        return "待再解";
      }
      if (this.doc.dirty) {
        return "软脏";
      }
      return "空闲";
    },
    barTone() {
      if (this.doc.initInFlight || this.doc.initHorizon) {
        return "tone-solving";
      }
      if (this.doc.solverAlive || this.doc.queued) {
        return "tone-solving";
      }
      if (this.doc.dirtyStructural) {
        return "tone-structural";
      }
      if (this.doc.pendingRequeue || this.doc.dirty) {
        return "tone-dirty";
      }
      return "tone-idle";
    },
    statusText() {
      const raw = this.doc.lastSolveStatus;
      if (!raw) {
        return "-";
      }
      return STATUS_MAP[raw] || raw;
    },
    statusTone() {
      const raw = this.doc.lastSolveStatus || "";
      if (raw === "ERROR" || raw === "APPLY_ERROR" || raw === "FAILED" || raw === "NULL") {
        return "is-error";
      }
      if (raw === "OPTIMAL" || raw === "FEASIBLE" || raw === "APPLIED") {
        return "is-ok";
      }
      if (raw === "ORDER_MODIFYING" || raw === "APPLY_LOCK_FAIL" || raw === "STALE_DISCARD" || raw === "STRUCTURAL_DISCARD") {
        return "is-warn";
      }
      return "";
    },
    dirtyTone() {
      if (this.doc.dirtyStructural) {
        return "is-error";
      }
      if (this.doc.dirty || this.doc.pendingRequeue) {
        return "is-warn";
      }
      return "is-ok";
    },
    dirtyText() {
      if (this.doc.dirtyStructural) {
        return this.doc.pendingRequeue ? "结构脏 / 待再解" : "结构脏";
      }
      if (this.doc.dirty) {
        return this.doc.pendingRequeue ? "软脏 / 待再解" : "软脏";
      }
      if (this.doc.pendingRequeue) {
        return "待再解";
      }
      return "干净";
    },
    eventAfterCount() {
      const fromDoc = this.doc.eventAfterCount;
      if (fromDoc != null) {
        return fromDoc;
      }
      return this.deltaEvents.length;
    },
    eventAfterText() {
      return this.eventAfterCount + " 条";
    },
    eventAfterTone() {
      if (this.doc.dirtyStructural && this.eventAfterCount > 0) {
        return "is-error";
      }
      if (this.eventAfterCount > 0) {
        return "is-warn";
      }
      return "is-ok";
    },
    structuralAfterCount() {
      return this.deltaEvents.filter((row) => row && row.structural).length;
    },
    detailItems() {
      const d = this.doc;
      return [
        {
          key: "date",
          label: "运营日",
          value: d.date || "-",
          hint: "一日一文，对应 Mongo _id"
        },
        {
          key: "epoch",
          label: "Epoch",
          value: this.displayNum(d.epoch),
          hint: "基线版本号，成功 apply 后递增"
        },
        {
          key: "solverAlive",
          label: "求解器搜索",
          value: this.doc.solverAlive ? "是（CpSolver 正在 search）" : "否",
          hint: "内存会话，CpSolver.solve 进行中才为是"
        },
        {
          key: "queued",
          label: "排队/执行中",
          value: this.doc.queued ? "是" : "否",
          hint: "在线求解线程已接管该日（排队或正在跑）"
        },
        {
          key: "initHorizon",
          label: "初始化区间",
          value: d.initHorizon ? "是（在线求解已暂停）" : "否",
          hint: "开始推导覆盖的日期，完成前不入队在线求解"
        },
        {
          key: "initInFlight",
          label: "正在初始化",
          value: d.initInFlight ? "是（订票/改签已拒绝）" : "否",
          hint: "该日正在重建 Init/Plan 并求解"
        },
        {
          key: "solving",
          label: "Epoch 求解中",
          value: this.doc.solving ? "是" : "否",
          hint: "beginSolve 后到 finish/apply 前的落库标记；无内存会话时视为僵死并自动清除"
        },
        {
          key: "pendingRequeue",
          label: "待再解",
          value: d.pendingRequeue ? "是" : "否",
          hint: "求解结束后是否等待重新入队"
        },
        {
          key: "dirty",
          label: "软脏",
          value: d.dirty ? "是" : "否",
          hint: "开解后又有事件，当前解可能仍可用"
        },
        {
          key: "dirtyStructural",
          label: "结构脏",
          value: d.dirtyStructural ? "是" : "否",
          hint: "派单/退票等结构变化，当前解必丢"
        },
        {
          key: "snapshotSeq",
          label: "快照序号",
          value: this.displayNum(d.snapshotSeq),
          hint: "开解时已提交的最大事件 seq"
        },
        {
          key: "eventAfterCount",
          label: "开解后事件",
          value: this.eventAfterText,
          hint: "seq 大于快照序号的增量，未纳入本轮解"
        },
        {
          key: "lastSolveStatus",
          label: "最近求解状态",
          value: d.lastSolveStatus ? `${this.statusText}（${d.lastSolveStatus}）` : "-",
          hint: "最近一次求解或 apply 结束状态"
        },
        {
          key: "snapshotAt",
          label: "开解快照时间",
          value: this.formatTime(d.snapshotAt),
          hint: "本轮 beginSolve 写入的快照时刻"
        },
        {
          key: "lastSolveStartedAt",
          label: "最近开解时间",
          value: this.formatTime(d.lastSolveStartedAt),
          hint: "在线 beginSolve 时间，不含批量初始化账本"
        },
        {
          key: "lastAppliedAt",
          label: "最近应用时间",
          value: this.formatTime(d.lastAppliedAt),
          hint: "最近一次成功 apply；丢弃的求解不会更新"
        },
        {
          key: "currentRunId",
          label: "当前 RunId",
          value: d.currentRunId || "-",
          hint: "当前求解 run，用于 stop / apply 校验",
          span: 2
        }
      ];
    }
  },
  watch: {
    date: {
      immediate: true,
      handler() {
        this.doc = emptyDoc(this.date);
        this.fetching = false;
        this.deltaEvents = [];
        this.deltaSnapshotSeq = 0;
        this.fetchEpoch();
      }
    },
    detailVisible(val) {
      if (val && this.detailTab === "events") {
        this.fetchEvents();
      }
    },
    detailTab(val) {
      if (this.detailVisible && val === "events") {
        this.fetchEvents();
      }
    }
  },
  mounted() {
    this.pollTimer = setInterval(() => {
      this.clock = Date.now();
      this.fetchEpoch();
    }, POLL_MS);
  },
  beforeDestroy() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  },
  methods: {
    displayNum(v) {
      return v == null ? 0 : v;
    },
    displayId(v) {
      return v == null || v === "" ? "-" : v;
    },
    sourceFrames(source) {
      if (!source) {
        return [];
      }
      return String(source).split(/\s*←\s*/).filter(Boolean);
    },
    sourceView(source) {
      const frames = this.sourceFrames(source);
      return frames.map((frame, idx) => ({
        frame,
        origin: idx === frames.length - 1 && frame !== "…"
      }));
    },
    copySource(source) {
      if (!source) {
        return;
      }
      Toolkit.copyContentH5(String(source));
      this.$message.success("来源已复制");
    },
    eventTypeMeta(type) {
      return EVENT_TYPE_MAP[type] || { label: type || "-", tag: "info" };
    },
    parseTime(v) {
      if (!v) {
        return 0;
      }
      if (typeof v === "number") {
        return v;
      }
      if (v instanceof Date) {
        return v.getTime();
      }
      const t = new Date(String(v).replace(/-/g, "/")).getTime();
      return isNaN(t) ? 0 : t;
    },
    formatDuration(ms) {
      if (ms == null || ms < 0) {
        return "-";
      }
      if (ms < 1000) {
        return Math.round(ms) + "ms";
      }
      if (ms < 60000) {
        return (ms / 1000).toFixed(1) + "s";
      }
      const min = Math.floor(ms / 60000);
      const sec = Math.round((ms % 60000) / 1000);
      return min + "min " + sec + "s";
    },
    formatTime(v) {
      if (!v) {
        return "-";
      }
      if (typeof v === "string") {
        return v;
      }
      try {
        return DateUtil.format(new Date(v), "yyyy-MM-dd hh:mm:ss");
      } catch (e) {
        return String(v);
      }
    },
    openDetail(tab) {
      this.detailTab = tab === "events" ? "events" : "status";
      this.detailVisible = true;
      this.fetching = false;
      this.fetchEpoch();
    },
    inspectPlan() {
      if (!this.date || this.inspecting) {
        return;
      }
      this.inspecting = true;
      getEvPlanInspect({ date: this.date })
        .then((res) => {
          this.inspectResult = (res && res.data) || null;
          this.inspectVisible = true;
          if (this.inspectResult && this.inspectResult.ok) {
            this.$message.success("规划检测通过");
          } else {
            this.$message.warning("规划检测未通过，请查看明细");
          }
        })
        .catch(() => { })
        .then(() => {
          this.inspecting = false;
        });
    },
    enqueueSolve() {
      if (!this.date || this.enqueueing || this.initPaused) {
        return;
      }
      const running = this.doc.solverAlive || this.doc.queued;
      const tip = running
        ? `当前 ${this.date} 正在求解或排队，将停止本轮搜索并加入下一轮。确认继续？`
        : `确认将 ${this.date} 加入求解队列？`;
      this.$confirm(tip, "立即求解", { type: "warning" })
        .then(() => {
          this.enqueueing = true;
          return submitEvOnlineSolve({ date: this.date });
        })
        .then((res) => {
          if (!res) {
            return;
          }
          this.$message.success("已加入求解队列");
          this.fetching = false;
          this.fetchEpoch();
        })
        .catch(() => { })
        .then(() => {
          this.enqueueing = false;
        });
    },
    fetchEpoch() {
      if (!this.date || this.fetching) {
        return;
      }
      const date = this.date;
      this.fetching = true;
      getEvPlanEpoch({ date })
        .then((res) => {
          if (this.date !== date) {
            return;
          }
          const data = (res && res.data) || emptyDoc(date);
          this.doc = Object.assign(emptyDoc(date), data);
          this.clock = Date.now();
          if (this.detailVisible && this.detailTab === "events") {
            this.fetchEvents();
          }
        })
        .catch(() => { })
        .then(() => {
          if (this.date === date) {
            this.fetching = false;
          }
        });
    },
    fetchEvents() {
      if (!this.date) {
        this.deltaEvents = [];
        this.deltaSnapshotSeq = 0;
        return;
      }
      const date = this.date;
      const reqId = this.eventsReqId + 1;
      this.eventsReqId = reqId;
      if (!this.deltaEvents.length) {
        this.eventsLoading = true;
      }
      getEvPlanDeltaEvents({ date })
        .then((res) => {
          if (this.date !== date || reqId !== this.eventsReqId) {
            return;
          }
          const data = (res && res.data) || {};
          this.deltaSnapshotSeq = data.snapshotSeq == null ? this.doc.snapshotSeq : data.snapshotSeq;
          this.deltaEvents = data.events || [];
          if (data.count != null) {
            this.doc = Object.assign({}, this.doc, { eventAfterCount: data.count });
          }
        })
        .catch(() => { })
        .then(() => {
          if (this.date === date && reqId === this.eventsReqId) {
            this.eventsLoading = false;
          }
        });
    }
  }
};
</script>

<style lang="less" scoped>
.epoch-dashboard {
  margin-top: 6px;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
  background: #fafafa;
  overflow: hidden;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
  }
}

.epoch-shell {
  display: flex;
  align-items: center;
}

.epoch-wheel {
  flex-shrink: 0;
  padding: 0 8px 0 2px;
}

.epoch-bar {
  display: flex;
  align-items: stretch;
  gap: 0;
  padding: 4px 10px;
  cursor: pointer;
  user-select: none;
  overflow: hidden;
  flex: 1;
  min-width: 0;
}

.epoch-bar-cells {
  display: flex;
  align-items: stretch;
  flex: 1;
  min-width: 0;
  overflow-x: auto;

  &::-webkit-scrollbar {
    height: 3px;
  }
}

.epoch-ledger {
  display: flex;
  align-items: stretch;
  gap: 0;
  padding: 4px 10px;
  cursor: pointer;
  user-select: none;
  overflow: hidden;
  flex: 1;
  min-width: 0;
}

.epoch-ledger-cells {
  display: flex;
  align-items: stretch;
  flex: 1;
  min-width: 0;
  overflow-x: auto;

  &::-webkit-scrollbar {
    height: 3px;
  }
}

.tone-idle {
  background: linear-gradient(135deg, #f6ffed 0%, #fafcfa 100%);
  border-color: #c2e7b0;

  &:hover {
    border-color: #67c23a;
  }
}

.tone-solving {
  background: linear-gradient(135deg, #ecf5ff 0%, #f5faff 100%);
  border-color: #b3d8ff;

  &:hover {
    border-color: #409eff;
  }
}

.tone-dirty {
  background: linear-gradient(135deg, #fdf6ec 0%, #fffaf3 100%);
  border-color: #f5dab1;

  &:hover {
    border-color: #e6a23c;
  }
}

.tone-structural {
  background: linear-gradient(135deg, #fef0f0 0%, #fff6f6 100%);
  border-color: #fbc4c4;

  &:hover {
    border-color: #f56c6c;
  }
}

.epoch-cell {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 12px;
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
  min-width: 72px;

  &:last-of-type {
    border-right: none;
  }
}

.is-run {
  min-width: 88px;
}

.cell-value-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.cell-label {
  font-size: 11px;
  color: #909399;
  line-height: 1.3;
  white-space: nowrap;
}

.cell-value {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
  white-space: nowrap;

  &.mono {
    font-variant-numeric: tabular-nums;
  }

  &.time {
    font-size: 12px;
    font-weight: 500;
  }

  &.is-ok {
    color: #67c23a;
  }

  &.is-warn {
    color: #e6a23c;
  }

  &.is-error {
    color: #f56c6c;
  }
}

.epoch-actions {
  margin-left: auto;
  align-self: center;
  flex-shrink: 0;
  padding-left: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.epoch-extra {
  margin-left: auto;
  align-self: center;
  flex-shrink: 0;
  padding-left: 12px;
  max-width: 58%;
}

.epoch-hint {
  align-self: center;
  font-size: 11px;
  color: #c0c4cc;
  white-space: nowrap;
  padding-left: 12px;
  flex-shrink: 0;
}

.pulse {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #409eff;
  box-shadow: 0 0 0 0 rgba(64, 158, 255, 0.5);
  animation: epoch-pulse 1.4s ease-out infinite;
  flex-shrink: 0;
}

.detail-tabs {
  /deep/ .el-tabs__header {
    margin-bottom: 12px;
  }
}

.detail-value {
  font-size: 13px;
  color: #303133;
  word-break: break-all;
}

.detail-hint {
  margin-top: 2px;
  font-size: 11px;
  color: #909399;
  line-height: 1.4;
}

.delta-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 8px;
}

.delta-meta {
  font-size: 12px;
  color: #909399;
}

.mono {
  font-variant-numeric: tabular-nums;
}

.source-stack {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.source-stack__body {
  flex: 1;
  min-width: 0;
  user-select: text;
  cursor: text;
}

.source-stack__line {
  font-size: 12px;
  line-height: 18px;
  color: #606266;
  word-break: break-all;

  &.is-origin {
    color: #303133;
    font-weight: 600;
  }
}

.source-stack__origin {
  margin-right: 4px;
}

.source-stack__arrow {
  display: inline-block;
  width: 14px;
  color: #c0c4cc;
}

.source-stack__copy {
  padding: 0;
  flex-shrink: 0;
  line-height: 18px;
}

.inspect-summary {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 13px;
}

.inspect-summary .ok {
  color: #67c23a;
  font-weight: 600;
}

.inspect-summary .bad {
  color: #f56c6c;
  font-weight: 600;
}

.inspect-summary .muted {
  color: #909399;
}

.inspect-text {
  margin: 0;
  max-height: 420px;
  overflow: auto;
  padding: 8px 10px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #303133;
  white-space: pre-wrap;
  word-break: break-all;
}

@keyframes epoch-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(64, 158, 255, 0.5);
  }

  70% {
    box-shadow: 0 0 0 6px rgba(64, 158, 255, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(64, 158, 255, 0);
  }
}
</style>
